import {
  DataGridPremiumProps,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import { useEffect, useMemo, useRef } from 'react';

type PropsType<R extends GridValidRowModel> = {
  dataGridProps: DataGridPremiumProps<R> &
    Required<
      Pick<DataGridPremiumProps<R>, 'apiRef' | 'getRowId' | 'getTreeDataPath'>
    >;
};

type UseDataGridTreeRowGroupingOutput<R extends GridValidRowModel> = {
  dataGridProps: DataGridPremiumProps<R>;
};

export const useDataGridTreeRowGrouping = <R extends GridValidRowModel>(
  props: PropsType<R>,
): UseDataGridTreeRowGroupingOutput<R> => {
  const { dataGridProps } = props;
  const { apiRef, sortingMode, getTreeDataPath, getRowId } = dataGridProps;
  const rows = useMemo(() => dataGridProps.rows ?? [], [dataGridProps.rows]);

  // The table data is stored in a ref so that the exact same reference is passed to the
  // `DataGridPremium` component on every render. This ref is never updated. Instead, the
  // rows are updated via the `updateRows` method of the `GridApiPremium` instance. This is
  // done primarily to avoid auto-collapsing any expanded rows when the table data changes.
  const initialData = useRef(rows);

  // Wrap `getRowId` in a ref so that it doesn't trigger the `useEffect` below on every render
  const getRowIdMemo = useRef(getRowId);

  useEffect(() => {
    if (!!apiRef.current) {
      // If we're using server mode, we can't use `updateRows` because the sorting comes out incorrect.
      // This happens because the DataGrid expects sorting to be handled externally (on the server), so
      // after the upsert occurs it does not re-apply the sort model. Instead we just set the rows
      // directly. This means that expansion state will not be preserved for server-side tables.
      if (sortingMode === 'server') {
        apiRef.current.setRows([...rows]);
        return;
      }

      if (!!apiRef.current.getRowModels) {
        // Get the IDs of all current data and identify which are excluded from the new data
        const curRows = apiRef.current.getRowModels();
        const curIds = curRows?.keys();
        const newIds = new Set(rows.map(getRowIdMemo.current));
        const removeIds = Array.from(curIds).filter((id) => !newIds.has(id));

        // Create delete actions for the removed IDs and merge them with the new data
        const deleteActions = removeIds.map((id) => ({
          ...curRows.get(id),
          _action: 'delete' as const,
        }));
        apiRef.current.updateRows([...rows, ...deleteActions]);
      }
    }
  }, [apiRef, rows, sortingMode]);

  // To avoid auto-collapsing of expanded rows, the `getTreeDataPath` prop needs to be the same
  // across renders. We wrap it in a ref so that the value provided during the first render is
  // used for all subsequent ones.
  const getTreeDataPathMemo = useRef(getTreeDataPath);

  return {
    dataGridProps: {
      ...dataGridProps,
      apiRef,
      rows: initialData.current,
      treeData: !!getTreeDataPath,
      getTreeDataPath: getTreeDataPathMemo.current,
    },
  };
};
